"use client"

import { useState } from "react"
import { Check } from "lucide-react"
import Link from "next/link"
import { Button } from "@/components/ui/button"
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Slider } from "@/components/ui/slider"
import { Modal, ModalContent, ModalTitle, ModalTrigger } from "@/components/ui/modal"
import HubspotForm from "@/components/ui/hubSpotForm"
import { sendGACustomEvent } from "@/lib/utils"
import { FAQ } from "@/sections/FAQ"
import { pricingPageFaq } from "@/constants"

export default function PricingPage() {
    const [adSpend, setAdSpend] = useState(10000)
    const [costPerClick, setCostPerClick] = useState(1.5)

    const monthlySavings = Math.round(adSpend * 0.25)
    const improvedCPC = (costPerClick * 0.6).toFixed(2)
    const annualImpact = monthlySavings * 12

    return (
        <div className="bg-white text-gray-900 min-h-screen dark:bg-black dark:text-white pt-14">
            <div className="container mx-auto px-4 py-16">
                <div className="text-center mb-16">
                    <h1 className="text-3xl font-bold mb-4 text-gray-900 dark:text-white">
                        Pricing Plans
                    </h1>
                    <p className="max-w-3xl mx-auto text-gray-600 dark:text-gray-400">
                        Choose the right plan to maximize your ad performance and eliminate wasted ad spend
                    </p>
                </div>

                <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                    {/* Free Plan */}
                    <Card className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 shadow-sm h-[500px] flex flex-col">
                        <CardHeader>
                            <CardTitle className="text-2xl text-gray-900 dark:text-white">Free</CardTitle>
                            <CardDescription className="text-gray-600 dark:text-gray-400">
                                Perfect for small businesses just getting started with paid ads
                            </CardDescription>
                            <div className="mt-4">
                                <span className="text-4xl font-bold text-gray-900 dark:text-white">$0</span>
                                <span className="text-gray-500 dark:text-gray-400 ml-2">/month</span>
                            </div>
                        </CardHeader>
                        <CardContent className="flex-grow">
                            <ul className="space-y-3">
                                {[
                                    // NOTE: We should add this after we include scheduling support for our workflows.
                                    // "Weekly negative keyword suggestions",
                                    "Analyze up to 5,000 search terms/month",
                                    "Email support",
                                ].map((item, idx) => (
                                    <li key={idx} className="flex items-start">
                                        <Check className="h-5 w-5 text-orange-500 mr-2 shrink-0 mt-0.5" />
                                        <span className="text-gray-700 dark:text-gray-300">{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </CardContent>
                        <CardFooter className="mt-auto">
                            <Link href="/sign-up" className="w-full">
                                <Button className="w-full bg-gray-100 hover:bg-gray-200 text-gray-900 border border-gray-300 dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-white dark:border-gray-600">
                                    Get Started
                                </Button>
                            </Link>
                        </CardFooter>
                    </Card>

                    {/* Pro Plan */}
                    <Card className="bg-white dark:bg-gray-900 border-orange-500 shadow-md relative h-[500px] flex flex-col">
                        <div className="absolute -top-4 left-0 right-0 flex justify-center">
                            <Badge className="bg-orange-500 text-white font-medium">MOST POPULAR</Badge>
                        </div>
                        <CardHeader>
                            <CardTitle className="text-2xl text-gray-900 dark:text-white">Pro</CardTitle>
                            <CardDescription className="text-gray-600 dark:text-gray-400">
                                For growing businesses serious about optimizing ad performance
                            </CardDescription>
                            <div className="mt-4">
                                <span className="text-4xl font-bold text-gray-900 dark:text-white">$19</span>
                                <span className="text-gray-500 dark:text-gray-400 ml-2">/month</span>
                            </div>
                        </CardHeader>
                        <CardContent className="flex-grow">
                            <ul className="space-y-3">
                                {[
                                    // NOTE: Include later
                                    // "Daily negative keyword monitoring",
                                    "Advanced performance dashboards",
                                    "Analyze up to 30,000 search terms/month",
                                    "Slack integration",
                                    "Priority email & chat support",
                                ].map((item, idx) => (
                                    <li key={idx} className="flex items-start">
                                        <Check className="h-5 w-5 text-orange-500 mr-2 shrink-0 mt-0.5" />
                                        <span className="text-gray-700 dark:text-gray-300">{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </CardContent>
                        <CardFooter className="mt-auto">
                            <Link href="/sign-up" className="w-full">
                                <Button className="w-full bg-orange-500 hover:bg-orange-600 text-white">
                                    Get Started
                                </Button>
                            </Link>
                        </CardFooter>
                    </Card>

                    {/* Agencies Plan */}
                    <Card className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 shadow-sm h-[500px] flex flex-col">
                        <CardHeader>
                            <CardTitle className="text-2xl text-gray-900 dark:text-white">
                                Agencies & Enterprises
                            </CardTitle>
                            <CardDescription className="text-gray-600 dark:text-gray-400">
                                For advertisers working at scale or agencies managing multiple accounts.
                            </CardDescription>
                            <div className="py-2 !-mb-4">
                                <span className="text-xl font-bold text-gray-900 dark:text-white">Custom pricing</span>
                            </div>
                        </CardHeader>
                        <CardContent className="flex-grow">
                            <ul className="space-y-3">
                                {[
                                    "Unlimited monthly ad spend",
                                    // NOTE: Include later
                                    // "Real-time negative keyword monitoring",
                                    "Support for multiple ad accounts",
                                    "Custom dashboards & reporting",
                                    "Analyze millions of search terms/month",
                                    "Slack integration",
                                    "24/7 priority support",
                                ].map((item, idx) => (
                                    <li key={idx} className="flex items-start">
                                        <Check className="h-5 w-5 text-orange-500 mr-2 shrink-0 mt-0.5" />
                                        <span className="text-gray-700 dark:text-gray-300">{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </CardContent>
                        <CardFooter className="mt-auto">
                            <Modal>
                                <ModalTrigger onClick={() => sendGACustomEvent('Talk to sales - hero section')} className="h-10 px-4 py-2 bg-gray-100 hover:bg-gray-200 w-full inline-flex items-center dark:text-white dark:bg-gray-800 dark:hover:bg-gray-700 justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border bg-background hover:text-accent-foreground rounded-md border-gray-300 dark:border-gray-700  hover:dark:bg-gray-800">
                                    Talk to Sales
                                </ModalTrigger>
                                <ModalContent className="max-md:w-[85%] max-sm:w-[94%]">
                                    <ModalTitle />
                                    <HubspotForm />
                                </ModalContent>
                            </Modal>
                        </CardFooter>
                    </Card>
                </div>

                {/* ROI Calculator */}
                <div className="mt-24 max-w-4xl mx-auto bg-white dark:bg-gray-900 rounded-xl p-8 border border-gray-200 dark:border-gray-700 shadow-sm">
                    <h2 className="text-3xl font-bold mb-6 text-center text-gray-900 dark:text-white">
                        Calculate Your Potential Savings
                    </h2>
                    <div className="grid md:grid-cols-2 gap-8">
                        <div>
                            <p className="text-gray-600 dark:text-gray-400 mb-6">
                                On average, Mardi helps businesses reduce wasted ad spend by 20-30% while improving cost per click by up to 40%.
                            </p>
                            <div className="space-y-6">
                                <div>
                                    <div className="flex justify-between mb-2">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Monthly Ad Spend</label>
                                        <span className="text-orange-500 font-medium">${adSpend.toLocaleString()}</span>
                                    </div>
                                    <Slider
                                        defaultValue={[10000]}
                                        max={50000}
                                        step={1000}
                                        onValueChange={(value) => setAdSpend(value[0])}
                                        className="w-full h-2 bg-gray-300 rounded-lg appearance-none cursor-pointer white-thumb-slider"
                                    />
                                    <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                                        <span>$1,000</span>
                                        <span>$50,000</span>
                                    </div>
                                </div>
                                <div>
                                    <div className="flex justify-between mb-2">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Current Cost Per Click</label>
                                        <span className="text-orange-500 font-medium">${costPerClick.toFixed(2)}</span>
                                    </div>
                                    <Slider
                                        defaultValue={[1.5]}
                                        max={5}
                                        step={0.1}
                                        onValueChange={(value) => setCostPerClick(value[0])}
                                        className="w-full h-2 bg-gray-300 rounded-lg  cursor-pointer white-thumb-slider"
                                    />
                                    <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                                        <span>$0.50</span>
                                        <span>$5.00</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
                            <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">Your Potential Results</h3>
                            <div className="space-y-4">
                                <div>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">Monthly Savings</p>
                                    <p className="text-2xl font-bold text-orange-500">${monthlySavings.toLocaleString()}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">Improved Cost Per Click</p>
                                    <p className="text-2xl font-bold text-orange-500">${improvedCPC}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">Annual Impact</p>
                                    <p className="text-2xl font-bold text-orange-500">${annualImpact.toLocaleString()}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* FAQ */}
                <div className="mt-24 max-w-4xl mx-auto">
                    <FAQ faqItems={pricingPageFaq} />
                </div>

                {/* CTA */}
                <div className="mt-24 text-center">
                    <h2 className="text-2xl md:text-4xl font-bold mb-6 text-gray-900 dark:text-white">
                        Ready to make every click count?
                    </h2>
                    <p className=" text-gray-600 dark:text-gray-400 max-w-2xl mx-auto mb-8">
                        Join hundreds of marketers who are saving time and money with Mardi's AI-powered optimization.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link href="/sign-up" >
                            <Button className="bg-primary text-white ">
                                Try Mardi Free
                            </Button>
                        </Link>
                        <Modal>
                            <ModalTrigger onClick={() => sendGACustomEvent('Schedule a Demo - CTA section')} className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border bg-background hover:text-accent-foreground h-11 rounded-md px-4 border-gray-300 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700">
                                Schedule a Demo
                            </ModalTrigger>
                            <ModalContent className="max-md:w-[85%] max-sm:w-[94%]">
                                <ModalTitle />
                                <HubspotForm />
                            </ModalContent>
                        </Modal>
                    </div>
                </div>
            </div>
        </div>
    )
}
